<template>
  <div class="layout-container">
    <el-container>
      <!-- Sidebar -->
      <el-aside :width="isCollapsed ? '64px' : '220px'" class="app-sidebar">
        <div class="sidebar-header">
          <div class="logo-area">
            <div class="logo-icon">
              <el-icon :size="isCollapsed ? 20 : 24"><House /></el-icon>
            </div>
            <transition name="fade">
              <span v-show="!isCollapsed" class="logo-text">双成果业管理</span>
            </transition>
          </div>
        </div>

        <el-menu
          :default-active="activeMenu"
          router
          :collapse="isCollapsed"
          class="sidebar-menu"
          :collapse-transition="false"
        >
          <el-menu-item index="/">
            <el-icon><House /></el-icon>
            <span>仪表盘</span>
          </el-menu-item>

          <el-sub-menu index="orders">
            <template #title>
              <el-icon><ShoppingCart /></el-icon>
              <span>订单管理</span>
            </template>
            <el-menu-item index="/product-orders">商品订单</el-menu-item>
            <el-menu-item index="/adoptions">认养订单</el-menu-item>
          </el-sub-menu>

          <el-sub-menu index="content">
            <template #title>
              <el-icon><Files /></el-icon>
              <span>内容管理</span>
            </template>
            <el-menu-item index="/lands">认养土地管理</el-menu-item>
            <el-menu-item index="/livestocks">认养禽畜管理</el-menu-item>
            <el-menu-item index="/articles">文章管理</el-menu-item>
            <el-sub-menu index="products-group">
              <template #title>商品管理</template>
              <el-menu-item index="/products?tab=fruit">果蔬</el-menu-item>
              <el-menu-item index="/products?tab=livestock">禽畜</el-menu-item>
            </el-sub-menu>
            <el-menu-item index="/addresses">地址管理</el-menu-item>
            <el-menu-item index="/activities">活动管理</el-menu-item>
          </el-sub-menu>

          <el-sub-menu index="settlement">
            <template #title>
              <el-icon><UserFilled /></el-icon>
              <span>入驻管理</span>
            </template>
            <el-menu-item index="/settlement/applications">入驻申请审核</el-menu-item>
            <el-menu-item index="/settlement/adoption-review">
              认养审核
              <el-badge
                v-if="adoptionPendingCount > 0"
                :value="adoptionPendingCount"
                :max="99"
                class="menu-badge"
              />
            </el-menu-item>
            <el-sub-menu index="farms-group">
              <template #title>农场管理</template>
              <el-menu-item index="/farms">农场列表</el-menu-item>
              <el-menu-item index="/farms/review">
                商品审核
                <el-badge
                  v-if="pendingReviewCount > 0"
                  :value="pendingReviewCount"
                  :max="99"
                  class="menu-badge"
                />
              </el-menu-item>
            </el-sub-menu>
          </el-sub-menu>

          <el-menu-item index="/users">
            <el-icon><User /></el-icon>
            <span>用户管理</span>
          </el-menu-item>

          <el-sub-menu index="system">
            <template #title>
              <el-icon><Setting /></el-icon>
              <span>系统管理</span>
            </template>
            <el-menu-item index="/system/help-articles">帮助中心管理</el-menu-item>
            <el-menu-item index="/system/about-us">关于我们管理</el-menu-item>
            <el-menu-item index="/system/banners">轮播图管理</el-menu-item>
            <el-menu-item index="/system/home-intro">简介管理</el-menu-item>
            <el-menu-item index="/system/settings">系统参数配置</el-menu-item>
          </el-sub-menu>

          <el-sub-menu index="finance">
            <template #title>
              <el-icon><CreditCard /></el-icon>
              <span>财务管理</span>
            </template>
            <el-menu-item index="/payments">支付记录</el-menu-item>
            <el-menu-item index="/payments/statistics">支付统计</el-menu-item>
            <el-menu-item index="/order-statistics">订单统计</el-menu-item>
            <el-menu-item index="/farm-commissions">农场提成</el-menu-item>
            <el-menu-item index="/farm-withdrawals">提现审核</el-menu-item>
          </el-sub-menu>

          <el-menu-item index="/operation-logs">
            <el-icon><Document /></el-icon>
            <span>操作日志</span>
          </el-menu-item>
        </el-menu>

        <!-- Collapse toggle -->
        <div class="sidebar-collapse" @click="isCollapsed = !isCollapsed">
          <el-icon><Fold v-if="!isCollapsed" /><Expand v-else /></el-icon>
        </div>
      </el-aside>

      <!-- Main area -->
      <el-container>
        <!-- Header -->
        <el-header class="app-header">
          <div class="header-left">
            <el-breadcrumb separator="/">
              <el-breadcrumb-item v-for="item in breadcrumbs" :key="item.path">
                {{ item.meta?.title || item.name }}
              </el-breadcrumb-item>
            </el-breadcrumb>
          </div>
          <div class="header-right">
            <el-dropdown @command="handleCommand">
              <span class="user-info">
                <el-avatar :size="32" :src="userAvatar" class="user-avatar" />
                <span class="username">{{ user?.username }}</span>
                <el-icon class="dropdown-arrow"><ArrowDown /></el-icon>
              </span>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item command="profile">
                    <el-icon><User /></el-icon>
                    个人信息
                  </el-dropdown-item>
                  <el-dropdown-item command="logout" divided>
                    <el-icon><SwitchButton /></el-icon>
                    退出登录
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </div>
        </el-header>

        <!-- Main Content -->
        <el-main class="app-main">
          <router-view />
        </el-main>
      </el-container>
    </el-container>

    <!-- 个人信息编辑对话框 -->
    <el-dialog
      v-model="profileVisible"
      title="个人信息"
      width="560px"
      :close-on-click-modal="false"
      destroy-on-close
    >
      <el-tabs v-model="profileTab">
        <el-tab-pane label="基本信息" name="basic">
          <el-form
            ref="profileFormRef"
            :model="profileForm"
            :rules="profileRules"
            label-width="80px"
          >
            <el-form-item label="账号" prop="username">
              <el-input v-model="profileForm.username" placeholder="登录账号" maxlength="64" />
            </el-form-item>
            <el-form-item label="姓名" prop="real_name">
              <el-input v-model="profileForm.real_name" placeholder="真实姓名" maxlength="50" />
            </el-form-item>
            <el-form-item label="手机号" prop="phone">
              <el-input v-model="profileForm.phone" placeholder="手机号" maxlength="11" />
            </el-form-item>
            <el-form-item label="邮箱" prop="email">
              <el-input v-model="profileForm.email" placeholder="邮箱" maxlength="120" />
            </el-form-item>
            <el-form-item label="地址" prop="address">
              <el-input v-model="profileForm.address" placeholder="地址" maxlength="200" />
            </el-form-item>
          </el-form>
        </el-tab-pane>

        <el-tab-pane label="修改密码" name="password">
          <el-form
            ref="passwordFormRef"
            :model="passwordForm"
            :rules="passwordRules"
            label-width="80px"
          >
            <el-form-item label="旧密码" prop="old_password">
              <el-input
                v-model="passwordForm.old_password"
                type="password"
                show-password
                placeholder="请输入旧密码"
              />
            </el-form-item>
            <el-form-item label="新密码" prop="new_password">
              <el-input
                v-model="passwordForm.new_password"
                type="password"
                show-password
                placeholder="请输入新密码（至少6位）"
              />
            </el-form-item>
            <el-form-item label="确认密码" prop="confirm_password">
              <el-input
                v-model="passwordForm.confirm_password"
                type="password"
                show-password
                placeholder="请再次输入新密码"
              />
            </el-form-item>
          </el-form>
        </el-tab-pane>
      </el-tabs>

      <template #footer>
        <el-button @click="profileVisible = false">取消</el-button>
        <el-button
          v-if="profileTab === 'basic'"
          type="primary"
          :loading="profileSaving"
          @click="handleSaveProfile"
        >
          保存
        </el-button>
        <el-button
          v-else
          type="primary"
          :loading="passwordSaving"
          @click="handleChangePassword"
        >
          确认修改
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  House, Files, UserFilled, User, OfficeBuilding, Setting,
  Fold, Expand, ArrowDown, SwitchButton, CreditCard, ShoppingCart, Document
} from '@element-plus/icons-vue'
import { useAuthStore } from '../stores/auth'
import { ElMessage, ElMessageBox } from 'element-plus'
import api from '../api'

export default {
  name: 'Layout',
  components: {
    House, Files, UserFilled, User, OfficeBuilding, Setting,
    Fold, Expand, ArrowDown, SwitchButton, CreditCard, ShoppingCart, Document
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const authStore = useAuthStore()
    const isCollapsed = ref(false)
    const pendingReviewCount = ref(0)
    const adoptionPendingCount = ref(0)

    const activeMenu = computed(() => {
      // 包含 query 参数以支持 /products?tab=fruit 等子菜单高亮
      if (route.query.tab) {
        return route.path + '?tab=' + route.query.tab
      }
      return route.path
    })
    const user = computed(() => authStore.user)
    const userAvatar = ref('')

    // ===== 个人信息编辑 =====
    const profileVisible = ref(false)
    const profileTab = ref('basic')
    const profileSaving = ref(false)
    const passwordSaving = ref(false)
    const profileFormRef = ref(null)
    const passwordFormRef = ref(null)

    const profileForm = ref({
      username: '',
      real_name: '',
      phone: '',
      email: '',
      address: ''
    })

    const passwordForm = ref({
      old_password: '',
      new_password: '',
      confirm_password: ''
    })

    const profileRules = {
      username: [
        { required: true, message: '请输入账号', trigger: 'blur' },
        { min: 2, max: 64, message: '账号长度为2-64个字符', trigger: 'blur' }
      ],
      phone: [
        { pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确', trigger: 'blur' }
      ],
      email: [
        { type: 'email', message: '邮箱格式不正确', trigger: 'blur' }
      ]
    }

    const validateConfirmPassword = (rule, value, callback) => {
      if (value !== passwordForm.value.new_password) {
        callback(new Error('两次输入的密码不一致'))
      } else {
        callback()
      }
    }

    const passwordRules = {
      old_password: [
        { required: true, message: '请输入旧密码', trigger: 'blur' }
      ],
      new_password: [
        { required: true, message: '请输入新密码', trigger: 'blur' },
        { min: 6, message: '新密码长度至少6位', trigger: 'blur' }
      ],
      confirm_password: [
        { required: true, message: '请再次输入新密码', trigger: 'blur' },
        { validator: validateConfirmPassword, trigger: 'blur' }
      ]
    }

    const openProfile = () => {
      profileVisible.value = true
      profileTab.value = 'basic'
      passwordForm.value = { old_password: '', new_password: '', confirm_password: '' }
      const u = authStore.user || {}
      profileForm.value = {
        username: u.username || '',
        real_name: u.real_name || '',
        phone: u.phone || '',
        email: u.email || '',
        address: u.address || ''
      }
    }

    const handleSaveProfile = async () => {
      if (!profileFormRef.value) return
      try {
        await profileFormRef.value.validate()
        profileSaving.value = true
        const res = await api.auth.updateProfile({ ...profileForm.value })
        if (res.data && res.data.success) {
          // 本地同步更新当前登录用户信息
          if (authStore.user) {
            authStore.user = { ...authStore.user, ...profileForm.value }
          }
          ElMessage.success(res.data.message || '个人信息已更新')
          profileVisible.value = false
        } else {
          ElMessage.error(res.data?.message || '保存失败')
        }
      } catch (e) {
        if (e && e.userMessage) {
          ElMessage.error(e.userMessage)
        } else if (e && e.message) {
          ElMessage.error(e.message)
        } else {
          ElMessage.error('保存失败')
        }
      } finally {
        profileSaving.value = false
      }
    }

    const handleChangePassword = async () => {
      if (!passwordFormRef.value) return
      try {
        await passwordFormRef.value.validate()
        passwordSaving.value = true
        const res = await api.auth.changePassword({
          old_password: passwordForm.value.old_password,
          new_password: passwordForm.value.new_password
        })
        if (res.data && res.data.success) {
          ElMessage.success('密码修改成功，请重新登录')
          profileVisible.value = false
          authStore.logoutLocal()
          router.push('/login')
        } else {
          ElMessage.error(res.data?.message || '密码修改失败')
        }
      } catch (e) {
        if (e && e.userMessage) {
          ElMessage.error(e.userMessage)
        } else if (e && e.message) {
          ElMessage.error(e.message)
        } else {
          ElMessage.error('密码修改失败')
        }
      } finally {
        passwordSaving.value = false
      }
    }

    const breadcrumbs = computed(() => {
      const matched = route.matched.filter(item => item.meta && item.meta.title)
      return matched
    })

    // 获取待审核商品数量
    let pollTimer = null
    const fetchPendingCount = async () => {
      try {
        const res = await api.pendingReviews.count()
        if (res.data.success) {
          pendingReviewCount.value = res.data.data.product_pending || 0
          adoptionPendingCount.value = (res.data.data.land_pending || 0) + (res.data.data.livestock_pending || 0)
        }
      } catch (e) {
        // 静默失败
      }
    }

    onMounted(() => {
      fetchPendingCount()
      // 每 30 秒刷新一次
      pollTimer = setInterval(fetchPendingCount, 30000)
    })

    onUnmounted(() => {
      if (pollTimer) clearInterval(pollTimer)
    })

    const handleCommand = async (command) => {
      if (command === 'logout') {
        try {
          await ElMessageBox.confirm('确定要退出登录吗？', '提示', {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          })
          await authStore.logout()
          router.push('/login')
        } catch (error) {
          // 用户取消
        }
      } else if (command === 'profile') {
        openProfile()
      }
    }

    return {
      activeMenu, user, userAvatar, breadcrumbs, isCollapsed,
      pendingReviewCount, adoptionPendingCount, handleCommand,
      profileVisible, profileTab, profileSaving, passwordSaving,
      profileFormRef, passwordFormRef, profileForm, passwordForm,
      profileRules, passwordRules, handleSaveProfile, handleChangePassword
    }
  }
}
</script>

<style scoped>
.layout-container {
  height: 100vh;
  overflow: hidden;
}

.el-container {
  height: 100%;
}

/* ============ Sidebar ============ */
.app-sidebar {
  background: #fff;
  border-right: 1px solid #f0f0f0;
  display: flex;
  flex-direction: column;
  transition: width 0.3s ease;
  overflow: hidden;
  position: relative;
  z-index: 10;
}

.sidebar-header {
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
}

.logo-area {
  display: flex;
  align-items: center;
  gap: 10px;
  overflow: hidden;
}

.logo-icon {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #07C160, #06AD56);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  flex-shrink: 0;
}

.logo-text {
  font-size: 18px;
  font-weight: 700;
  color: #07C160;
  white-space: nowrap;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.sidebar-menu {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  border-right: none !important;
  padding: 8px 0;
}

.sidebar-menu:not(.el-menu--collapse) {
  width: 220px;
}

.sidebar-menu.el-menu--collapse {
  width: 64px;
}

/* ============ 主分类（顶层独立菜单项） ============ */
.sidebar-menu > .el-menu-item {
  height: 44px; line-height: 44px; margin: 2px 8px; border-radius: 8px;
  color: #555; font-size: 14px; font-weight: 400; transition: all 0.2s ease;
}
.sidebar-menu > .el-menu-item:hover { background-color: #E8F8EE !important; color: #07C160 !important; }
.sidebar-menu > .el-menu-item.is-active { background-color: #E8F8EE !important; color: #07C160 !important; font-weight: 600; border-right: none; }

/* ============ 一级分类（顶层子菜单标题） ============ */
.sidebar-menu > .el-sub-menu > .el-sub-menu__title {
  height: 44px; line-height: 44px; margin: 2px 8px; border-radius: 8px;
  color: #444; font-size: 14px; font-weight: 600; letter-spacing: 0.5px; transition: all 0.2s ease;
}
.sidebar-menu > .el-sub-menu > .el-sub-menu__title:hover { background-color: #E8F8EE !important; color: #07C160 !important; }

/* ============ 二级分类（嵌套菜单项） ============ */
.sidebar-menu .el-menu--inline .el-menu-item {
  height: 40px; line-height: 40px; margin: 1px 8px 1px 16px; border-radius: 6px;
  background-color: transparent; color: #777; font-size: 13px; font-weight: 400;
  padding-left: 48px !important; transition: all 0.2s ease;
}
.sidebar-menu .el-menu--inline .el-menu-item:hover { background-color: #f5f5f5 !important; color: #07C160 !important; }
.sidebar-menu .el-menu--inline .el-menu-item.is-active { background-color: #E8F8EE !important; color: #07C160 !important; font-weight: 600; }

/* 嵌套子菜单标题（如商品管理、农场管理）也按二级分类样式 */
.sidebar-menu .el-sub-menu .el-sub-menu .el-sub-menu__title {
  height: 40px; line-height: 40px; margin: 1px 8px 1px 16px; border-radius: 6px;
  color: #777; font-size: 13px; font-weight: 400; padding-left: 48px !important; transition: all 0.2s ease;
}
.sidebar-menu .el-sub-menu .el-sub-menu .el-sub-menu__title:hover { background-color: #f5f5f5 !important; color: #07C160 !important; }

/* Sidebar collapse toggle */
.sidebar-collapse {
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-top: 1px solid #f0f0f0;
  cursor: pointer;
  color: #999;
  transition: color 0.2s;
  flex-shrink: 0;
}

.sidebar-collapse:hover {
  color: #07C160;
}

/* ============ Header ============ */
.app-header {
  height: 64px !important;
  background: #fff;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 32px;
}

.header-left {
  flex: 1;
}

.header-left .el-breadcrumb {
  font-size: 14px;
}

.header-right {
  display: flex;
  align-items: center;
}

.user-info {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 4px 12px;
  border-radius: 8px;
  transition: background-color 0.2s;
}

.user-info:hover {
  background-color: #f5f5f5;
}

.user-avatar {
  flex-shrink: 0;
}

.username {
  margin: 0 8px;
  color: #333;
  font-size: 14px;
  font-weight: 500;
}

.dropdown-arrow {
  font-size: 12px;
  color: #999;
}

/* ============ Main Content ============ */
.app-main {
  padding: 24px;
  background-color: #f5f7fa;
  overflow-y: auto;
  height: calc(100vh - 64px);
}

/* Menu collapse transitions */
.sidebar-menu .el-menu-item span,
.sidebar-menu .el-sub-menu__title span {
  transition: opacity 0.2s ease;
}

/* Badge in menu item */
.menu-badge {
  margin-left: 8px;
}
.menu-badge :deep(.el-badge__content) {
  top: 50%;
  transform: translateY(-50%);
}
</style>
